import { useAuth0 } from '@auth0/auth0-vue'
import { EvaluationContext } from '@openfeature/web-sdk'
import {
  FeatureFlagProviderConfig,
  TrafficType,
} from '@thyme/libs/src/flags/types'
import { requiredInject } from '@thyme/libs/src/vue/inject'
import { computed, onBeforeUnmount, watch } from 'vue'
import { APP_CONFIG_KEY } from '@/libs/config/types'
import { useFlagStore } from '@/libs/flags'
import { ENTITY_ID_CLAIM } from '@/libs/state/auth0Client'
import { useWhoAmI } from '@/pages/HomePage/queries'

/**
 * Sets up use of the feature flag client that is aware of the auth and whoAmI state.
 * Any time there is a change in the traffic key to be used with the feature flag provider,
 * the client is re-initialized, which will reactively update all flags.
 * @returns isLoading - reactive loading state for /whoami request
 */
export function useFeatureFlagAuth() {
  const auth0 = useAuth0()
  const config = requiredInject(APP_CONFIG_KEY)
  const entityId = computed(() => auth0.user.value?.[ENTITY_ID_CLAIM])

  const { data: whoAmI, isLoading } = useWhoAmI(entityId)

  const contractName = computed(() => whoAmI.value?.contractName)

  watch(
    [isLoading, contractName],
    async ([loading, contractName]) => {
      // Don't initialize until /whoami completes to avoid:
      // 1. PROVIDER_NOT_READY errors from initializing too early
      // 2. Re-initialization churn when contractName changes from undefined -> value
      if (loading) {
        return
      }

      // PHI WARNING: Use non-identifiable info only.
      // Do not send any identifiable information like email or auth-adjacent information like entity id
      const nonSensitiveAttributes: EvaluationContext = contractName
        ? { contract: contractName }
        : {}

      await useFlagStore().init(config.flags as FeatureFlagProviderConfig, {
        targetingKey: undefined,
        trafficType: TrafficType.CONNECT_USER_SESSION,
        attributes: nonSensitiveAttributes,
      })
    },
    { immediate: true }
  )

  onBeforeUnmount(async () => {
    await useFlagStore().destroy()
  })

  return {
    isLoading,
  }
}
